<template>
  <div
    class="location-selector"
    :class="{ 'is-focus': isFocus, 'is-show': !showlocation }"
    v-click-outside="onClickOutside"
  >
    <y-text
      v-if="showLabel"
      class="title"
      font="var(--y-regular-font)"
      color="#949494"
      size="14px"
    >
      {{ t("shared.hotel.search.location") }}
    </y-text>
    <el-autocomplete
      :disabled="locationDisabled"
      v-model="locationRef"
      :fetch-suggestions="querySearch"
      clearable
      hide-loading
      class="location-input"
      valueKey="showLabel"
      :placeholder="t('shared.hotel.search.locationPlaceholder')"
      @select="handleSelect"
      @blur="handleBlur"
      @focus="handleFocus"
      @input="handleInput"
      ref="autocompleteRef"
    >
      <template #prefix>
        <font-icon class="search-icon" icon="icon-Location" />
      </template>
      <template #default="{ item }">
        <div class="item flex">
          <y-icon :name="iconMaps[item.locationType]" />
          <div class="item-label ml-2">
            {{ item.showLabel }}
            <div
              class="hotel-name-en"
              v-if="item.showHotelEnName && item.nameEn"
            >
              {{ item.nameEn }}
            </div>
          </div>
        </div>
      </template>
    </el-autocomplete>

    <div class="popover-trigger" ref="popoverTriggerRef" />
    <el-popover
      v-if="popoverToggle"
      ref="popoverRef"
      popper-class="location-popover"
      :virtual-ref="popoverTriggerRef"
      :visible="isPopoverVisible"
      placement="bottom-start"
      virtual-triggering
      :arrow-offset="32"
      width="504"
      @show="handlePopverOpen"
    >
      <LocationPopoverContent @select="handlePopoverSelect" />
    </el-popover>
  </div>
</template>

<script setup>
import common from "@charmdeer/common";
import { ClickOutside as vClickOutside } from "element-plus";
import _ from "lodash";
import { computed, onActivated, onMounted, ref } from "vue";
import { nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import { searchUniversalViaPost } from "@/api/biz/common";
import { getRestCity, getRestTpHotel } from "@/api/rest/common";
import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";
import { useToggles } from "@/hooks/useToggles";
import { useUserStoreHook } from "@/store/modules/user";
import { queryToParams } from "@/utils/tool";

import LocationPopoverContent from "./LocationPopoverContent/index.vue";
const { t } = useI18n();
const router = useRouter();
const userStore = useUserStoreHook();
const YHotelStore = useHotelStoreHook();
const _queryString = ref("");
const popoverToggle = useToggles("Y_WEB_SEARCH_LOCATION_POPOVER");
defineProps({
  showLabel: {
    type: Boolean,
    default: true
  },
  modelValue: {
    type: String,
    default: ""
  },
  locationDisabled: {
    type: Boolean,
    default: false
  },
  showlocation: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(["update:modelValue", "change"]);

const locationRef = ref(
  _.get(YHotelStore, "hotelSearchModel.location._id", null)
);
const popoverRef = ref(null);
const isPopoverVisible = ref(false);
const isFocus = ref(false);
const autocompleteRef = ref(null);
const popoverTriggerRef = ref(null);
const emitBySelect = ref(false);
const showHotelEnName = computed(() => {
  return userStore.language !== "en";
});

const iconMaps = {
  googleMapPolitical: "icon-location",
  city: "icon-location",
  moduleTpHotel: "icon-hotel",
  search: "icon-search"
};

const querySearch = async (queryString, cb) => {
  if (_queryString.value === queryString) {
    cb([]);
    return;
  }
  emitBySelect.value = false;
  _queryString.value = queryString;

  if (!queryString || queryString === "null") {
    cb([]);
    emit("change");
    return;
  }

  if (isPopoverVisible.value) {
    closePopover();
  }

  const result = [];
  const res = await searchUniversalViaPost(queryString);
  const { city = [], moduleTpHotel = [], googleMapPolitical = [] } = res;

  city.forEach(item => {
    item.locationType = "city";
    item.showLabel = common.city.toString.name(item, userStore.language);
    item.nameEn = common.city.toString.country(item, userStore.language);
    item.showHotelEnName = true;
  });
  result.push(...city);

  const googleMapPoliticalResult = [];
  if (googleMapPolitical.length) {
    googleMapPolitical.forEach(item => {
      const hasCity = city.some(
        cityItem => cityItem.showLabel === item.description
      );
      if (!hasCity) {
        item.locationType = "googleMapPolitical";
        item.showLabel = item.description;
        item._id = item.place_id;
        googleMapPoliticalResult.push(item);
      }
    });
    result.push(...googleMapPoliticalResult);
  }

  const searchItem = {
    locationType: "search",
    showLabel: queryString,
    _id: queryString
  };

  if (googleMapPoliticalResult.length > 0) {
    result.push(searchItem);
  } else {
    result.unshift(searchItem);
  }

  moduleTpHotel.forEach(item => {
    item.locationType = "moduleTpHotel";
    item.showLabel = item.name;
    item.showHotelEnName = showHotelEnName.value;
  });
  result.push(...moduleTpHotel);

  cb(result);
};

const handlePopoverSelect = async item => {
  let resultItem = null;
  try {
    if (item.type === "city") {
      const cityData = item.data;
      const id = cityData._id;
      resultItem = await locationValueMaps.city.getDefaultValue(id);
    } else if (item.type === "history") {
      const historyData = item.data;

      if (historyData.target) {
        const id = historyData.target.ref;
        const type = historyData.target.collectionName;

        if (type === "hotel" || type === "moduleTpHotel") {
          resultItem = await locationValueMaps.moduleTpHotel.getDefaultValue(
            id
          );
        } else if (type === "city") {
          resultItem = await locationValueMaps.city.getDefaultValue(id);
        }
      } else if (historyData.info) {
        const type = historyData.info.collectionName;

        if (type === "google") {
          const id = historyData.info.place_id;
          const showLabel = historyData.info.description;
          resultItem =
            await locationValueMaps.googleMapPolitical.getDefaultValue(
              id,
              showLabel
            );
        } else if (type === "keyword") {
          const id = historyData.text;
          const showLabel = historyData.text;
          resultItem = await locationValueMaps.search.getDefaultValue(
            id,
            showLabel
          );
        }
      }
    }

    if (item.type === "history" && !resultItem) {
      const id = item.data.text;
      const showLabel = item.data.text;
      resultItem = await locationValueMaps.search.getDefaultValue(
        id,
        showLabel
      );
    }
  } catch (error) {
    const text = item.data?.text || item.data?.city || "";
    if (text) {
      resultItem = await locationValueMaps.search.getDefaultValue(text, text);
    }
  }

  if (resultItem && resultItem.showLabel) {
    locationRef.value = resultItem.showLabel;
    _queryString.value = resultItem.showLabel || "";
    handleSelect(resultItem);
    closePopover();
    nextTick(() => {
      if (autocompleteRef.value) {
        autocompleteRef.value.blur();
      }
    });
  }
};

const handleSelect = item => {
  emitBySelect.value = true;
  emit("update:modelValue", item._id);
  emit("change", item);
};

const handleBlur = async () => {
  isFocus.value = false;
  if (emitBySelect.value) {
    return;
  }
  const currentLocationLabel =
    locationValueMaps[
      YHotelStore.hotelSearchModel.location.locationType
    ]?.locationValue?.();

  if (locationRef.value && locationRef.value !== currentLocationLabel) {
    const item = {
      locationType: "search",
      showLabel: locationRef.value,
      _id: locationRef.value
    };
    handleSelect(item);
  } else if (!locationRef.value) {
    emit("update:modelValue", null);
    emit("change", null);
  }
};

const locationValueMaps = {
  city: {
    locationValue: () => {
      return common.city.toString.name(
        YHotelStore.hotelSearchModel.location,
        userStore.language
      );
    },
    getDefaultValue: async _id => {
      const _city = await getRestCity(_id);
      return {
        ..._city,
        locationType: "city",
        showLabel: common.city.toString.name(_city, userStore.language)
      };
    }
  },
  moduleTpHotel: {
    locationValue: () => {
      return common.moduleTpHotel.toString.name(
        YHotelStore.hotelSearchModel.location,
        userStore.language
      );
    },
    getDefaultValue: async _id => {
      const _hotel = await getRestTpHotel(_id);
      return {
        ..._hotel,
        locationType: "moduleTpHotel",
        showLabel: common.moduleTpHotel.toString.name(
          _hotel,
          userStore.language
        )
      };
    }
  },
  googleMapPolitical: {
    locationValue: () => {
      return YHotelStore.hotelSearchModel.location.showLabel;
    },
    getDefaultValue: async (_id, showLabel) => {
      return {
        locationType: "googleMapPolitical",
        place_id: _id,
        description: showLabel,
        _id,
        showLabel
      };
    }
  },
  search: {
    locationValue: () => {
      return YHotelStore.hotelSearchModel.location.showLabel;
    },
    getDefaultValue: async (_id, showLabel) => {
      return {
        locationType: "search",
        description: showLabel,
        _id,
        showLabel
      };
    }
  }
};

const init = async () => {
  if (locationRef.value) {
    if (YHotelStore.hotelSearchModel.location) {
      const { locationType } = YHotelStore.hotelSearchModel.location;
      const _locationValue = locationValueMaps[locationType]?.locationValue?.();
      if (_locationValue) {
        locationRef.value = _locationValue;
      }
    }
  } else {
    const _model = queryToParams() || {};
    const { locationType, _id, showLabel } = _model.location;
    const result = await locationValueMaps[locationType]?.getDefaultValue?.(
      _id,
      showLabel
    );
    if (result && result._id) {
      locationRef.value = result.showLabel;
      handleSelect(result);
    }
  }
  YHotelStore.getSearchHistory();
};
const handlePopverOpen = () => {
  YHotelStore.getSearchHistory();
};
const handleInput = value => {
  if (value) {
    closePopover();
  } else {
    openPopover();
  }
};
const handleFocus = event => {
  isFocus.value = true;
  if (!locationRef.value) {
    openPopover();
  }
  nextTick(() => {
    const input = event.target;
    input.setSelectionRange(input.value.length, input.value.length);
  });
};

const openPopover = () => {
  isPopoverVisible.value = true;
};

const closePopover = () => {
  isPopoverVisible.value = false;
};

const onClickOutside = event => {
  const popoverEl = popoverRef.value?.popperRef?.contentRef;
  if (!popoverEl) {
    closePopover();
    return;
  }
  if (popoverEl.contains(event.target)) {
    return;
  }

  closePopover();
};
onMounted(() => {
  init();
});
onActivated(() => {
  const _pageForward = router.options.history.state.forward;
  if (!_pageForward) {
    init();
  }
});
</script>

<style lang="scss" scoped>
::v-global(.location-popover) {
  --el-popover-padding: 0 !important;
}
.location-selector {
  --input-direction: rtl;
  width: 188px;
  position: relative;
  .search-icon {
    font-size: 18px;
    color: #000000;
  }

  .title {
    margin-bottom: 6px;
    line-height: 1;
    display: block;
  }
  :deep(.el-input.is-disabled .el-input__wrapper) {
    box-shadow: none;
  }
  :deep(.el-input__wrapper) {
    box-shadow: none;
    padding-left: 0;
    padding-right: 0;

    .el-input__inner {
      color: var(--y-text-color);
      font-size: 18px;
      line-height: 20px;
      font-family: var(--y-medium-font);
      direction: var(--input-direction);
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &.is-focus {
    --input-direction: ltr;
  }
}
.is-show {
  width: 0;
  height: 0;
  opacity: 0;
}
.item {
  line-height: 1;
  padding: 10px 0;
  .y-icon {
    margin-top: 1px;
  }
  .item-label {
    line-height: 16px;
  }
  .hotel-name-en {
    font-weight: 400;
    font-size: 14px;
    color: #949494;
    margin-top: 8px;
    line-height: 16px;
  }
}
.popover-trigger {
  position: absolute;
  width: 1px;
  height: 100%;
  left: -32px;
  top: 0;
  bottom: 0;
  z-index: -1;
  pointer-events: none;
}
</style>
